/**
 * The booklet block vocabulary, shared by the browser and the server.
 *
 * A booklet is not prose. It is a sequence of typed blocks — explanation, a
 * worked example, a boxed set of key facts, an exam tip in the margin — and it
 * is that recurring furniture, not the writing, that makes a revision guide
 * feel like a revision guide rather than a wall of generated text.
 *
 * Four things have to agree on what a block is: the generator (which builds
 * the tool schema from BLOCK_TYPES), the validator below, the section editor
 * in the browser, and the renderer that prints it. They agree by all reading
 * this file — loaded as a plain script in the browser and required in Node,
 * the same way subjects.js and a11y-profiles.js are.
 *
 * Everything here treats its input as untrusted. Blocks arrive from a language
 * model and are stored as JSON, so validate*() drops what it does not
 * recognise instead of throwing: one malformed block loses that block, not the
 * whole section the teacher just waited two minutes for. This is the same
 * defensive treatment cleanGlossary() gives model output in worksheetGen.js.
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.BookletSchema = factory();
}(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    // Drawn diagrams are validated by the module that draws them. Optional in
    // the browser: a page that only reads booklets loads the renderer's half
    // and a stored spec is still normalised by it.
    const CurveDiagramPresets = (typeof module === 'object' && module.exports)
        ? require('./curve-diagram-presets.js')
        : (typeof window !== 'undefined' ? window.CurveDiagramPresets : null);
    const CurveDiagram = (typeof module === 'object' && module.exports)
        ? require('./curve-diagram.js')
        : (typeof window !== 'undefined' ? window.CurveDiagram : null);

    // Caps exist so a runaway generation cannot bloat blocks_json past what
    // MySQL's JSON column and the print renderer can sensibly hold. They are
    // deliberately generous — hitting one means something went wrong, not that
    // a teacher wrote a lot.
    const LIMITS = {
        heading: 300,
        text: 4000,
        short: 300,
        term: 120,
        definition: 600,
        listItems: 12,
        pointItems: 10,
        point: 700,
        steps: 10,
        tableRows: 20,
        tableCols: 8,
        blocksPerSection: 24,
        exercisesPerSection: 12,
        partsPerExercise: 8,
    };

    /**
     * Key terms are marked inline as [[term]] and rendered as the coloured,
     * underlined words a revision guide is full of.
     *
     * Deliberately not markdown: stripMarkdownEmphasis() in claude.js removes
     * `**`, `__` and `*` from every model field on the way in, so any of those
     * would be silently eaten before the renderer ever saw them. Double square
     * brackets pass through decodeFieldsDeep untouched.
     */
    var KEY_TERM_RE = /\[\[([^\[\]]{1,80})\]\]/g;

    /** The plain words of a string, with the key-term markers taken off. */
    function stripKeyTerms(s) {
        return String(s == null ? '' : s).replace(KEY_TERM_RE, '$1');
    }

    /**
     * Every block the format supports.
     *
     * `label` is what the section editor shows. `fields` drives both the tool
     * schema handed to the model and the validator, so a new block type needs
     * adding in exactly one place.
     *
     * `flow` blocks run in the main column; `aside` blocks are pulled into the
     * margin by the print stylesheet. The renderer needs to know which is
     * which, and so does the model — an exam tip written as if it were a
     * paragraph reads wrong once it is set in a 60mm margin.
     *
     * `points` leads the list because it is what the format is actually made
     * of. A revision guide is numbered points, not paragraphs: the numbering
     * is how a pupil finds their place again, and it is what stops the writing
     * drifting into essay prose that nobody revises from.
     */
    const BLOCK_TYPES = [
        {
            id: 'points',
            label: 'Numbered points',
            placement: 'flow',
            purpose: 'The teaching itself, as numbered points. This is the backbone of a section — most of it should be these.',
            fields: { title: 'short', items: 'longlist' },
        },
        {
            id: 'prose',
            label: 'Lead-in',
            placement: 'flow',
            purpose: 'A sentence or two setting up the points that follow. Sparingly — never several in a row.',
            fields: { text: 'text' },
        },
        {
            id: 'example',
            label: 'Example',
            placement: 'flow',
            purpose: 'A concrete "for example" — the specific case that makes an abstract point land. Boxed.',
            fields: { text: 'text' },
        },
        {
            id: 'key_facts',
            label: 'Key facts',
            placement: 'flow',
            purpose: 'The must-learn points, boxed. Recall, not explanation.',
            fields: { title: 'short', items: 'list' },
        },
        {
            id: 'worked_example',
            label: 'Worked example',
            placement: 'flow',
            purpose: 'A question worked through in numbered steps, ending in an answer.',
            fields: { question: 'text', steps: 'list', answer: 'short' },
        },
        {
            id: 'definition',
            label: 'Definition',
            placement: 'flow',
            purpose: 'A term the specification uses, defined precisely. Collected into the glossary at the back.',
            fields: { term: 'term', definition: 'definition' },
        },
        {
            id: 'exam_tip',
            label: 'Exam tip',
            placement: 'aside',
            purpose: 'What the examiner is actually looking for. One or two sentences.',
            fields: { text: 'short' },
        },
        {
            id: 'common_mistake',
            label: 'Common mistake',
            placement: 'aside',
            purpose: 'The error pupils reliably make here, and what to do instead.',
            fields: { text: 'short' },
        },
        {
            id: 'table',
            label: 'Table',
            placement: 'flow',
            purpose: 'Comparisons and classifications, where a list would lose the structure.',
            fields: { title: 'short', headers: 'list', rows: 'rows' },
        },
        {
            id: 'equation',
            label: 'Equation',
            placement: 'flow',
            // LaTeX, rendered by the same KaTeX path worksheets use, so
            // $inline$ and $$block$$ behave identically across the product.
            purpose: 'A formula to learn, in LaTeX, with a line saying what each symbol means.',
            fields: { latex: 'short', meaning: 'short' },
        },
        {
            id: 'diagram',
            label: 'Diagram',
            placement: 'flow',
            // The model never supplies the image. It describes what the
            // diagram should show; the image pass searches for candidates and
            // a vision call picks one. `query` is what gets searched.
            purpose: 'A picture that carries information the text cannot. Describe what it must show.',
            fields: { query: 'short', caption: 'short' },
        },
        {
            id: 'curve_diagram',
            label: 'Drawn diagram',
            placement: 'flow',
            // Unlike `diagram`, nothing is searched for: the model picks a
            // preset or describes the curves, and curve-diagram.js draws it.
            // So the picture always arrives, and always says what the text says.
            purpose: 'A diagram of curves on a pair of axes, drawn exactly rather than searched for: where curves cross, how one shifts, what area is shaded (a supply and demand diagram, an AD/AS diagram, cost and revenue curves). Because it is drawn from what you specify, the text around it may refer to its labels.',
            fields: { diagram: 'curve', caption: 'short' },
        },
        {
            id: 'quick_check',
            label: 'Quick check',
            placement: 'flow',
            purpose: 'Two or three one-line questions with answers, to break up the reading. Not the workbook.',
            fields: { questions: 'qa' },
        },
        {
            id: 'roundup',
            label: 'Round-up',
            placement: 'flow',
            // The joke is the point. A revision guide that is relentlessly
            // earnest does not get read; the gag at the foot of the page is
            // what makes a pupil turn over. It always comes last, which the
            // renderer enforces rather than trusting the model's ordering.
            purpose: 'Closes the section: a light-hearted one-line title, then two sentences on what actually matters. Exactly one, at the very end.',
            fields: { quip: 'short', summary: 'text' },
        },
    ];

    const BLOCK_IDS = BLOCK_TYPES.map(function (b) { return b.id; });
    const BLOCK_BY_ID = BLOCK_TYPES.reduce(function (acc, b) { acc[b.id] = b; return acc; }, {});

    /**
     * The workbook's question tiers, in the order they are printed.
     *
     * The graduation is the product: a pupil who cannot yet do an exam
     * question can still do the warm-up, and the tier tells the teacher which
     * is which without reading them. `exam_style` questions are built from the
     * real board question *shapes* in data/question-patterns/, so they carry
     * genuine command words and mark allocations.
     */
    const EXERCISE_TIERS = [
        { id: 'warmup', label: 'Warm-up', purpose: 'Recall. One mark, one fact, answerable straight from the section.' },
        { id: 'practice', label: 'Practice', purpose: 'Application. Uses the section, but needs a step of reasoning.' },
        { id: 'exam_style', label: 'Exam-style', purpose: 'A real board question shape, with command words and marks.' },
    ];

    const TIER_IDS = EXERCISE_TIERS.map(function (t) { return t.id; });

    // No page count here, deliberately. How many pages a booklet runs to
    // depends on how many diagrams, tables and exercises the sections turn out
    // to need, which is not known until they are written – the old '12–18
    // pages' labels were a guess presented as a fact. targetSections and
    // wordsPerSection ARE facts: they are what the generator asks the model
    // for, so blurb states them and nothing else.
    const LENGTHS = [
        { id: 'brief', label: 'Brief', targetSections: 5, wordsPerSection: 350, blurb: 'A single topic, covered once over.' },
        { id: 'standard', label: 'Standard', targetSections: 10, wordsPerSection: 500, blurb: 'A full topic with room to explain it.' },
        { id: 'extended', label: 'Extended', targetSections: 18, wordsPerSection: 650, blurb: 'A whole module, in depth.', proOnly: true },
    ];

    const LENGTH_IDS = LENGTHS.map(function (l) { return l.id; });

    function lengthFor(id) {
        for (var i = 0; i < LENGTHS.length; i++) if (LENGTHS[i].id === id) return LENGTHS[i];
        return LENGTHS[1];
    }

    // ── field cleaners ───────────────────────────────────────────────────
    // Each returns a cleaned value, or null when the field is unusable. A null
    // in a required field drops the whole block.

    function cleanString(v, max) {
        if (typeof v !== 'string') {
            // Models occasionally hand back a number where a string was asked
            // for (marks, an answer of "7"). That is recoverable; an object is
            // not.
            if (typeof v === 'number' && isFinite(v)) v = String(v);
            else return null;
        }
        var s = v.trim().slice(0, max);
        return s || null;
    }

    function cleanList(v, maxItems, maxLen) {
        if (!Array.isArray(v)) return null;
        var out = [];
        for (var i = 0; i < v.length && out.length < maxItems; i++) {
            var s = cleanString(v[i], maxLen);
            if (s) out.push(s);
        }
        return out.length ? out : null;
    }

    function cleanRows(v, cols) {
        if (!Array.isArray(v)) return null;
        var out = [];
        for (var i = 0; i < v.length && out.length < LIMITS.tableRows; i++) {
            var row = cleanList(v[i], cols, LIMITS.short);
            if (!row) continue;
            // Ragged rows would render as a broken table, so pad rather than
            // drop: the model is usually right about the content and wrong
            // only about trailing empty cells.
            while (row.length < cols) row.push('');
            out.push(row.slice(0, cols));
        }
        return out.length ? out : null;
    }

    function cleanQa(v) {
        if (!Array.isArray(v)) return null;
        var out = [];
        for (var i = 0; i < v.length && out.length < 4; i++) {
            var item = v[i];
            if (!item || typeof item !== 'object') continue;
            var q = cleanString(item.question, LIMITS.short);
            var a = cleanString(item.answer, LIMITS.short);
            // Both halves or neither — a question with no answer is worse than
            // no question, because the teacher cannot mark it.
            if (q && a) out.push({ question: q, answer: a });
        }
        return out.length ? out : null;
    }

    function cleanField(kind, value) {
        switch (kind) {
            case 'text': return cleanString(value, LIMITS.text);
            case 'short': return cleanString(value, LIMITS.short);
            case 'term': return cleanString(value, LIMITS.term);
            case 'definition': return cleanString(value, LIMITS.definition);
            case 'list': return cleanList(value, LIMITS.listItems, LIMITS.short);
            case 'longlist': return cleanList(value, LIMITS.pointItems, LIMITS.point);
            case 'qa': return cleanQa(value);
            // A model's { preset, options } and a stored spec both come through
            // here; either way what is kept is the plain spec.
            case 'curve':
                if (CurveDiagramPresets) return CurveDiagramPresets.resolve(value);
                return CurveDiagram ? CurveDiagram.normalize(value) : null;
            default: return null;
        }
    }

    /**
     * One block, cleaned — or null if it cannot be rendered.
     *
     * A block survives only if EVERY field its type declares came back usable.
     * Half a worked example (steps but no answer) is not a partial success; it
     * prints as a question the pupil cannot check, so it is dropped.
     */
    function validateBlock(raw) {
        if (!raw || typeof raw !== 'object') return null;
        var def = BLOCK_BY_ID[raw.type];
        if (!def) return null;

        var out = { type: def.id };

        if (def.id === 'table') {
            var headers = cleanList(raw.headers, LIMITS.tableCols, LIMITS.short);
            if (!headers) return null;
            var rows = cleanRows(raw.rows, headers.length);
            if (!rows) return null;
            out.headers = headers;
            out.rows = rows;
            // Optional throughout: a table with no caption is fine.
            var tTitle = cleanString(raw.title, LIMITS.short);
            if (tTitle) out.title = tTitle;
            return out;
        }

        for (var name in def.fields) {
            if (!Object.prototype.hasOwnProperty.call(def.fields, name)) continue;
            var value = cleanField(def.fields[name], raw[name]);
            if (value === null) {
                // `title` and `caption` are labels, not content — a key-facts
                // box with no title still teaches, so it is kept untitled.
                if (name === 'title' || name === 'caption') continue;
                return null;
            }
            out[name] = value;
        }
        // The home-language equivalent of a defined term, when the booklet was
        // built for an EAL pupil. Optional in every sense: it is only asked
        // for when a language was chosen, the model is told to leave it out
        // rather than invent a word for a term with no equivalent, and a
        // definition without one is a complete definition. So it is kept
        // outside the loop above, where a missing field kills the block.
        if (def.id === 'definition') {
            var translation = cleanString(raw.translation, LIMITS.term);
            if (translation) out.translation = translation;
        }
        return out;
    }

    /**
     * Every renderable block from one section's generation, in order.
     *
     * The round-up is pulled to the end and capped at one however the model
     * ordered them. It is the closing gag; a joke in the middle of the
     * teaching reads as a mistake, and two of them read as a bug.
     */
    function validateBlocks(raw) {
        if (!Array.isArray(raw)) return [];
        var out = [];
        var roundup = null;
        var hasDiagram = false;
        for (var i = 0; i < raw.length && out.length < LIMITS.blocksPerSection; i++) {
            var block = validateBlock(raw[i]);
            if (!block) continue;
            if (block.type === 'roundup') {
                if (!roundup) roundup = block;
                continue;
            }
            // One picture per section, and the first one asked for wins. A
            // section carries a single image_url, so the image pass searches
            // for the first diagram block and nothing else; a second one was
            // kept, stored, and then printed as a caption-less nothing. It is
            // dropped here so the section that reaches the page is the section
            // that was actually rendered.
            if (block.type === 'diagram') {
                if (hasDiagram) continue;
                hasDiagram = true;
            }
            out.push(block);
        }
        if (roundup) out.push(roundup);
        return out;
    }

    /**
     * One workbook exercise, cleaned — or null if unusable.
     *
     * `parts` is how a multi-part exam question is carried: each part has its
     * own marks and its own answer, so the answer key at the back can line up
     * part-for-part with the question at the front.
     */
    // ── Question text: tables and parts ──────────────────────
    //
    // The exercise schema has no table field, so a question that needs one
    // carries it as markdown pipes inside its text, and the parts of a question
    // are a list the renderer letters itself. The model does not always keep to
    // either: it writes tables without their outer pipes, labels parts "(a)"
    // itself, and copies the parts into the stem as well. These are shared by
    // the validator (new questions) and the renderers (questions already
    // stored), so both read a question the same way.

    /**
     * A line's pipe positions, ignoring any inside $maths$ — "|x|" is an
     * absolute value, not a column. Masked to spaces rather than removed, so
     * positions in the masked line are positions in the real one.
     */
    function pipeCells(line) {
        var masked = line.replace(/\$\$[\s\S]*?\$\$|\$[^$\n]*\$/g, function (m) {
            return m.replace(/\|/g, ' ');
        });
        if (masked.indexOf('|') === -1) return null;
        var bounded = /^\s*\|.*\|\s*$/.test(masked);
        var cells = [];
        var from = 0;
        for (var i = 0; i < masked.length; i++) {
            if (masked.charAt(i) !== '|') continue;
            cells.push(line.slice(from, i));
            from = i + 1;
        }
        cells.push(line.slice(from));
        if (bounded) cells = cells.slice(1, -1);
        return { bounded: bounded, cells: cells.map(function (c) { return c.trim(); }) };
    }

    /**
     * Text split into plain runs and the pipe tables inside it.
     *
     * A table is a run of lines that either all start and end with a pipe, or
     * — the way the model often writes them — are two or more lines that all
     * split into the same number (two or more) of cells. A separator row of
     * dashes is dropped; the first row is the header.
     */
    function pipeTableSegments(str) {
        var lines = String(str == null ? '' : str).split(/\r?\n/);
        var segments = [];
        var text = [];

        function flushText() {
            var chunk = text.join('\n').trim();
            text = [];
            if (chunk) segments.push({ type: 'text', text: chunk });
        }

        var i = 0;
        while (i < lines.length) {
            var run = [];
            var j = i;
            while (j < lines.length) {
                var row = pipeCells(lines[j]);
                if (!row || row.cells.length < 2) break;
                run.push(row);
                j++;
            }
            var allBounded = run.length && run.every(function (r) { return r.bounded; });
            var width = run.length ? run[0].cells.length : 0;
            var even = run.length >= 2 && run.every(function (r) { return r.cells.length === width; });
            if (run.length && (allBounded || even)) {
                var rows = run.map(function (r) { return r.cells; }).filter(function (cells) {
                    return !cells.every(function (c) { return /^:?-{2,}:?$/.test(c); });
                });
                if (rows.length) {
                    flushText();
                    segments.push({ type: 'table', rows: rows });
                }
                i = j;
            } else {
                text.push(lines[i]);
                i++;
            }
        }
        flushText();
        return segments;
    }

    function hasPipeTable(str) {
        return pipeTableSegments(str).some(function (s) { return s.type === 'table'; });
    }

    // "(a) ", "a) ", "(ii) " at the start of a part: the renderer letters the
    // parts itself, so a label the model wrote in prints as "a) (a) …".
    var PART_LABEL_RE = /^\s*(?:\(\s*(?:[a-h]|i{1,3}|iv|vi{0,3}|ix|x)\s*\)|(?:[a-h]|i{1,3}|iv|vi{0,3})\))\s*/i;

    function stripPartLabel(str) {
        return String(str == null ? '' : str).replace(PART_LABEL_RE, '');
    }

    /**
     * The stem of a parted question, without the parts copied into it.
     *
     * Cut at the first line that starts "(a)" or "a)", or where the first
     * part's own wording begins — whichever comes first. What is left is the
     * shared context the stem is meant to be; a stem that was nothing but a
     * copy of its parts comes back empty.
     */
    function stemWithoutParts(stem, parts) {
        var text = String(stem == null ? '' : stem);
        if (!parts || !parts.length) return text;
        var cut = -1;
        var label = /(^|\n)[ \t]*\(?a\)[ \t]/i.exec(text);
        if (label) cut = label.index + label[1].length;
        var first = stripPartLabel(parts[0] && parts[0].question).trim().slice(0, 40);
        if (first.length >= 15) {
            var at = text.indexOf(first);
            if (at !== -1 && (cut === -1 || at < cut)) cut = at;
        }
        if (cut === -1) return text;
        return text.slice(0, cut).trim();
    }

    /** An exercise as it should be shown: no labels on its parts, no copies of them in its stem. */
    function tidyExercise(ex) {
        if (!ex || !Array.isArray(ex.parts) || !ex.parts.length) return ex;
        var parts = ex.parts.map(function (p) {
            return Object.assign({}, p, { question: stripPartLabel(p.question) });
        });
        return Object.assign({}, ex, { question: stemWithoutParts(ex.question, parts), parts: parts });
    }

    // "The table shows…", "Complete the table", "the table below": a question
    // that points at a table and needs one on the page to be answerable.
    var TABLE_REF_RE = /\b(?:the|this) table\b|\btable (?:below|above)\b/i;

    function validateExercise(raw) {
        if (!raw || typeof raw !== 'object') return null;
        if (TIER_IDS.indexOf(raw.tier) === -1) return null;

        var question = cleanString(raw.question, LIMITS.text);
        if (!question) return null;

        var out = { tier: raw.tier, question: question };

        var marks = Number(raw.marks);
        if (isFinite(marks) && marks > 0) out.marks = Math.min(Math.round(marks), 30);

        if (Array.isArray(raw.parts) && raw.parts.length) {
            var parts = [];
            for (var i = 0; i < raw.parts.length && parts.length < LIMITS.partsPerExercise; i++) {
                var p = raw.parts[i];
                if (!p || typeof p !== 'object') continue;
                var pq = cleanString(p.question, LIMITS.text);
                var pa = cleanString(p.answer, LIMITS.text);
                if (!pq || !pa) continue;
                var part = { question: pq, answer: pa };
                var pm = Number(p.marks);
                if (isFinite(pm) && pm > 0) part.marks = Math.min(Math.round(pm), 30);
                parts.push(part);
            }
            if (!parts.length) return null;   // declared parts but none usable

            var tidy = tidyExercise({ question: out.question, parts: parts });
            out.question = tidy.question;
            parts = tidy.parts;

            // A part that points at a table nobody wrote cannot be answered, so
            // it goes — and so does the whole question if its stem does. The
            // table may be in the stem, or in this part or one before it.
            var tableSoFar = hasPipeTable(out.question);
            if (!tableSoFar && TABLE_REF_RE.test(out.question)
                && !parts.some(function (pt) { return hasPipeTable(pt.question); })) return null;
            var kept = parts.filter(function (pt) {
                if (hasPipeTable(pt.question)) tableSoFar = true;
                return tableSoFar || !TABLE_REF_RE.test(pt.question);
            });
            if (!kept.length) return null;
            if (kept.length < parts.length && kept.every(function (pt) { return pt.marks; })) {
                out.marks = Math.min(kept.reduce(function (sum, pt) { return sum + pt.marks; }, 0), 30);
            }
            out.parts = kept;
            // A parted question's own `answer` is redundant and would print
            // twice in the key, so it is deliberately not carried.
            return out;
        }

        // Same rule as a part: no pointing at a table that is not there.
        if (TABLE_REF_RE.test(question) && !hasPipeTable(question)) return null;

        var answer = cleanString(raw.answer, LIMITS.text);
        if (!answer) return null;            // unmarkable, see cleanQa
        out.answer = answer;
        return out;
    }

    /**
     * A section's exercises, ordered by tier so the workbook always graduates
     * warm-up → practice → exam-style regardless of what order they came back
     * in. Ties keep generation order, which is the order the model chose to
     * build difficulty within a tier.
     */
    function validateExercises(raw) {
        if (!Array.isArray(raw)) return [];
        var out = [];
        for (var i = 0; i < raw.length && out.length < LIMITS.exercisesPerSection; i++) {
            var ex = validateExercise(raw[i]);
            if (ex) { ex._i = out.length; out.push(ex); }
        }
        out.sort(function (a, b) {
            return (TIER_IDS.indexOf(a.tier) - TIER_IDS.indexOf(b.tier)) || (a._i - b._i);
        });
        return out.map(function (ex) { delete ex._i; return ex; });
    }

    /** Every `definition` block across a booklet, deduped, for the glossary. */
    function collectGlossary(sections) {
        if (!Array.isArray(sections)) return [];
        var seen = Object.create(null);
        var out = [];
        for (var i = 0; i < sections.length; i++) {
            var blocks = (sections[i] && sections[i].blocks) || [];
            for (var j = 0; j < blocks.length; j++) {
                if (blocks[j].type !== 'definition') continue;
                // The glossary is a reference list, not running text, so the
                // key-term markers come off rather than being rendered as
                // highlights inside an entry that is already a definition.
                var term = stripKeyTerms(blocks[j].term);
                var key = term.toLowerCase();
                if (seen[key]) continue;
                seen[key] = true;
                var entry = { term: term, definition: stripKeyTerms(blocks[j].definition) };
                if (blocks[j].translation) entry.translation = blocks[j].translation;
                out.push(entry);
            }
        }
        return out.sort(function (a, b) { return a.term.localeCompare(b.term); });
    }

    /**
     * The same sections with every workbook answer removed.
     *
     * This is what stands between a pupil sitting a lesson and the answer key
     * printed at the back of the workbook. It runs server-side, on the way
     * out, in the same shape as stripAnswers() in routes/student.js — never in
     * the browser, where "the page does not draw it" is not the same thing as
     * "the page was not sent it".
     *
     * Guide blocks are deliberately left alone. A `worked_example` and a
     * `quick_check` are teaching content, printed in the revision guide the
     * pupil would otherwise be reading on paper; removing them would take the
     * lesson away to protect the exercises.
     *
     * Returns a deep copy. Mutating rows loaded from the database would let one
     * request strip the answers out of a cached object another request marks
     * against.
     */
    function stripExerciseAnswers(sections) {
        if (!Array.isArray(sections)) return [];
        return sections.map(function (section) {
            var copy = JSON.parse(JSON.stringify(section));
            if (!Array.isArray(copy.exercises)) {
                copy.exercises = [];
                return copy;
            }
            copy.exercises = copy.exercises.map(function (ex) {
                delete ex.answer;
                if (Array.isArray(ex.parts)) {
                    ex.parts = ex.parts.map(function (p) { delete p.answer; return p; });
                }
                return ex;
            });
            return copy;
        });
    }

    return {
        LIMITS,
        KEY_TERM_RE,
        stripKeyTerms,
        BLOCK_TYPES,
        BLOCK_IDS,
        BLOCK_BY_ID,
        EXERCISE_TIERS,
        TIER_IDS,
        LENGTHS,
        LENGTH_IDS,
        lengthFor,
        validateBlock,
        validateBlocks,
        validateExercise,
        validateExercises,
        pipeTableSegments,
        hasPipeTable,
        stripPartLabel,
        stemWithoutParts,
        tidyExercise,
        collectGlossary,
        stripExerciseAnswers,
    };
}));
